'use client';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { AccordionProps } from '@/components/accordion/types';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import Icon from '@/components/icon/Icon';
import { Link } from '@/components/link/Link';
import './style.css';
import { renameDomProps } from '@/utils/sanitizeDomProps';

export const variantClasses = {
  primary: {
    accordion: 'border-b border-neutral-100 pb-6',
    details: 'bg-transparent',
    summary: 'text-[20px] font-medium leading-7',
    content: 'mt-5 pr-12 text-base font-normal leading-7',
  },
  secondary: {
    accordion: 'pt-3',
    details: 'bg-white px-6 rounded-lg border',
    summary: 'text-[18px] py-6 font-medium leading-6',
    content: 'mt-1 pr-8 text-[16px] pb-6 font-normal leading-6',
  },
  tertiary: {
    accordion: 'border-b border-neutral-100 py-[10px]',
    details: 'pl-[18px] pr-[10px] bg-transparent',
    summary: 'text-[20px] font-medium leading-7',
    content: 'mt-5 pr-12 text-base font-normal leading-7',
  },
};

function updateView(
  setIsMobileView: React.Dispatch<React.SetStateAction<boolean>>,
) {
  setIsMobileView(window.innerWidth < 768); // Adjust breakpoint as needed
}

export interface ExtendedAccordionProps extends AccordionProps {
  onToggle: () => void;
  index: number;
  isOpen: boolean;
}

export function Accordion({
  title,
  description,
  summaryClass = '',
  contentClass = '',
  variant = 'primary',
  link,
  theme,
  onToggle,
  index,
  isOpen,
  iconVariant,
  ...rest
}: ExtendedAccordionProps) {
  const [isMobileView, setIsMobileView] = useState(false);
  const fallbackTheme = theme ?? 'light';
  const fallbackVariant = variant ?? 'primary';
  const isDark = fallbackTheme === 'dark';
  useEffect(() => {
    updateView(setIsMobileView); // Set initial view
    window.addEventListener('resize', () => updateView(setIsMobileView)); // Add resize listener
    return () =>
      window.removeEventListener('resize', () => updateView(setIsMobileView)); // Cleanup listener
  }, []);

  const renamedProps = renameDomProps(rest);

  return (
    <div
      data-component-id="C001"
      className={clsx(
        'smzh-accordion',
        fallbackVariant,
        {
          [variantClasses[fallbackVariant]?.accordion]:
            fallbackVariant !== 'secondary' || index !== 0, // Remove padding for the first secondary item
        },
        isDark &&
          (fallbackVariant === 'primary' || fallbackVariant === 'tertiary') &&
          'border-white/15',
      )}
      {...renamedProps}
    >
      <details
        open={isOpen}
        className={clsx(
          'cursor-pointer',
          isDark ? 'text-white' : 'text-gray-900',
          variantClasses[fallbackVariant]?.details,
          isDark &&
            fallbackVariant === 'secondary' &&
            'border-white/10 bg-brand-700',
        )}
        onClick={(e) => {
          e.preventDefault();
          onToggle();
        }}
      >
        <summary
          className={clsx(
            'flex flex-row items-start justify-between gap-8 sm:gap-32',
            variantClasses[fallbackVariant]?.summary,
            summaryClass,
            fallbackTheme === 'light' ? 'text-brand-700' : 'text-white',
          )}
          aria-expanded={isOpen}
          aria-controls={`accordion-content-${index}`}
          id={`accordion-summary-${index}`}
          role="button"
        >
          <span className="hyphens-auto break-words text-base font-[450]">
            {title}
          </span>
          <div className="flex-shrink-0">
            <Icon
              className={clsx(
                'smzh-accordion-icon transition-transform duration-300',
                {
                  'rotate-180': iconVariant === 'arrow' && isOpen,
                },
              )}
              name={
                iconVariant === 'plus'
                  ? isOpen
                    ? 'minus'
                    : 'plus'
                  : 'chevron-down'
              }
              size={24}
              aria-label="Toggle menu"
            />
          </div>
        </summary>
        <motion.div
          id={`accordion-content-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: isMobileView ? 0.7 : 0.3 }} // Adjust transition duration based on view
          className={clsx(
            variantClasses[fallbackVariant]?.content,
            contentClass,
            'overflow-hidden',
            isDark ? 'content-dark' : 'text-neutral-600',
          )}
        >
          <div>
            <SerializeDescription description={description} />
            {link && (
              <div className="mt-4">
                <Link {...link} />
              </div>
            )}
          </div>
        </motion.div>
      </details>
    </div>
  );
}
